import type { CategoryType } from "@habity/types";
import type {
  Estate,
  House,
  Condominium,
  Plot,
  Cottage,
  Farm,
  HousingCooperative,
  VitecEstateType,
  CommercialProperty,
} from "@habity/types/vitec";

export const isHouse = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is House => {
  return estate_type === "House";
};

export const isCondominium = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is Condominium => {
  return estate_type === "Condominium";
};

export const isCottage = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is Cottage => {
  return estate_type === "Cottage";
};

export const isFarm = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is Farm => {
  return estate_type === "Farm";
};

export const isHousingCooperative = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is HousingCooperative => {
  return estate_type === "HousingCooperative";
};

export const isPlot = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is Plot => {
  return estate_type === "Plot";
};

export const isCommercialProperty = (
  estate_data: Estate,
  estate_type: VitecEstateType,
): estate_data is CommercialProperty => {
  return estate_type === "CommercialProperty";
};

export const isNewConstruction = (
  estate_data: Estate,
  estate_type: VitecEstateType
) => {
  if (
    isHouse(estate_data, estate_type) ||
    isHousingCooperative(estate_data, estate_type) ||
    isCondominium(estate_data, estate_type) ||
    isCottage(estate_data, estate_type)
  ) {
    const { baseInformation } = estate_data || {};
    const { newConstruction } = baseInformation || {};
    return !!newConstruction;
  }
  return false;
};

export const mapCategory = (
  estate_data: Estate,
  estate_type: VitecEstateType,
  excludeNewConstruction = false
): CategoryType | undefined => {
  // Check special condition for new construction first
  if (isNewConstruction(estate_data, estate_type) && !excludeNewConstruction) {
    return "new_construction";
  }
  // Explicitly check for townhouses before anything else (they could be House, HousingCooperative etc)
  const { marketedAs } = estate_data || {};
  if (marketedAs === "TwinHouse") {
    return "twinhouse";
  }
  if (marketedAs === "Townhouse") {
    return "townhouse";
  }
  if (marketedAs === "TerraceHouse") {
    return "terraced";
  }
  if (isHousingCooperative(estate_data, estate_type)) {
    return "apartment";
  }
  if (isHouse(estate_data, estate_type)) {
    return "house";
  }
  if (isCottage(estate_data, estate_type)) {
    return "cottage";
  }
  if (isFarm(estate_data, estate_type)) {
    return "farm";
  }
  if (isCommercialProperty(estate_data, estate_type)) {
    return "commercial";
  }
  if (isPlot(estate_data, estate_type)) {
    return "plot";
  }
  if (isCondominium(estate_data, estate_type)) {
    return "apartment";
  }

  return undefined;
};
